/* eslint-disable react/jsx-max-depth */
import { type FC, type ReactNode } from "react";

import { FocusContextProvider } from "~/components/common/focus-context/FocusContextProvider";
import { PrintAreaProvider } from "~/components/common/print-area/PrintAreaProvider";

import { QueryBuilderStateProvider } from "../editor/cells/QueryBuilderStateProvider";
import { EaselStateContext } from "./EaselContext";
import { RuntimeProvider } from "./runtime/RuntimeProvider";
import { type UseCreateEaselOptions, useCreateEasel } from "./useCreateEasel";

/**
 * Easel
 *
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠿⠿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡟⠛⠛⢻⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠛⠛⠛⠛⠓⠒⠒⠚⠛⠛⠛⠛⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⡿⠀⣾⣿⣿⣿⡏⢹⣿⣿⣿⣷⠀⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⠇⢀⣿⣿⣿⣿⡇⢸⣿⣿⣿⣿⡀⠸⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⡿⠀⣸⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣇⠀⢿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣷⣶⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣶⣾⣿⣿⣿⣿⣿⣿⣿⣿
 *
 * @description
 * Easel is a context wrapper for a canvas.
 *
 * @purpose
 * In order to enable better communication / updates between various
 * libraries that are interdependent:
 * - Observable Runtime
 * - ProseMirror (Editor State)
 * - Canvas API
 * - Canvas Parameters
 * - Moment Global Object
 *
 * Easel is __NOT__ a replacement for Redux.
 *
 * What should go in Redux?
 * - Global/application level state should still be stored in Redux.
 * - State values that should persist between any canvas (such as user related preferences)
 *
 * What should go in Easel?
 * - State values that directly affect Observable Runtime or ProseMirror.
 * - Hooks for updating state values (either in Easel or Redux) that might
 *   be used by descendants of easel.
 *
 * Future Goals:
 * - Easel will make placing a canvas within an iframe easier since it
 * will provide a consistent API for the canvas to communicate with the parent.
 */

export type EaselProps = {
	children?: ReactNode;
} & UseCreateEaselOptions;

export const Easel: FC<EaselProps> = ({ children, ...config }) => {
	const easelState = useCreateEasel(config);

	return (
		<PrintAreaProvider>
			<EaselStateContext.Provider value={easelState}>
				<FocusContextProvider>
					<QueryBuilderStateProvider
						queryBuilderStateManager={easelState.queryBuilderStateManager}
					>
						<RuntimeProvider runtime={easelState.runtime}>{children}</RuntimeProvider>
					</QueryBuilderStateProvider>
				</FocusContextProvider>
			</EaselStateContext.Provider>
		</PrintAreaProvider>
	);
};
